// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.AddRolePopup.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * AddUserPopup Component
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';

import OverflowText from 'share/components/controls/Izenda.OverflowText';
import { Table, Column } from 'share/components/controls/DataGrid/Izenda.Table';
import Paginator from 'share/components/controls/DataGrid/helpers/Izenda.Paginator';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import Input from 'share/components/controls/Izenda.Input';
import SearchTextBox from 'share/components/controls/Izenda.SearchTextBox';
import RoleListStore from 'logic/Izenda.RoleListStore';

const CANCEL_TEXT = 'cancel';
const ACCEPT_TEXT = 'accept';

@connect()
class AddRoleGrid extends Component {
  static propTypes = {
    store: PropTypes.object
  };

  constructor(props: any) {
    super(props);
    const { store } = props;

    this.search = store.search.bind(store);
    this.goToPage = store.goToPage.bind(store);
    this.onSortChange = store.onSortChange.bind(store);
    this.setSearchValue = store.setSearchValue.bind(store);
    this.onPageSizeChange = store.onPageSizeChange.bind(store);
  }

  componentDidMount() {
    this.props.store.loadAllRoles();
  }

  /**
   * Render select cell
   */
  renderSelectCell = (data: any): any => {
    const onChange = event => (data.selected = event.target.checked);
    return <Input type="checkbox" checked={data.selected} onChange={onChange} />;
  };

  renderRoleCell = (data: any): any => {
    return <OverflowText tooltip={data.name} text={data.name} />;
  };

  /**
   * Render table component
   */
  renderTable = (): any => {
    const { store, localization } = this.props;

    const selectRowDefinition = {
      key: 'selected',
      width: 30,
      title: '',
      sortable: false,
      columnKey: 'selected',
      fixed: true,
      headerComponent: Input,
      headerComponentProps: {
        ...store.allCheckboxStatus,
        onChange: store.toggleAllRoles.bind(store),
        type: 'checkbox'
      }
    };

    const roleDefinition = {
      key: 'name',
      width: 320,
      title: localization.getText('ROLE_SETUP_LABEL_ROLES'),
      sortable: true,
      columnKey: 'name'
    };

    return (
      <Table
        dataSource={store.roles}
        onSortChange={this.onSortChange}
        draggableTable={false}
        sortInfo={store.pageInfo.colSortDirs}
      >
        <Column {...selectRowDefinition} cell={this.renderSelectCell} />
        <Column {...roleDefinition} cell={this.renderRoleCell} />
      </Table>
    );
  };

  /**
   * Handle key press
   * @param  {Event} event
   */
  handleKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      this.search();
    }
  };

  render() {
    const { store: { pageInfo, searchText } } = this.props;

    return (
      <GridLayout>
        <GridLayout.Row>
          <GridLayout.Col cols="5">
            <SearchTextBox
              clickable
              value={searchText}
              onClick={this.search}
              className="u-posRelative"
              onChange={this.setSearchValue}
              onKeyPress={this.handleKeyPress}
            />
          </GridLayout.Col>
        </GridLayout.Row>
        <GridLayout.Row>
          <GridLayout.Col cols="12">{this.renderTable()}</GridLayout.Col>
        </GridLayout.Row>
        <GridLayout.Row>
          <GridLayout.Col cols="12">
            <Paginator
              onChange={this.goToPage}
              onDropdownChange={this.onPageSizeChange}
              totalRecords={pageInfo.totalRecords}
              pageSize={pageInfo.pageSize}
              pageIndex={pageInfo.pageIndex}
              isDirty={this.checkDirty}
            />
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    );
  }
}

@connect()
export default class AddRolePopup extends Component {
  static propTypes = {
    store: PropTypes.object,
    onChange: PropTypes.func.isRequired
  };

  constructor(props: any) {
    super(props);
    this.roleListStore = new RoleListStore(props.loadRoles, props.roleOption);
  }

  /**
   * Handle when popup closing
   * @param  {Object} closeCallback function
   * @param  {Object} type of close button click
   */
  onClosing = (closeCallback: any, type: string) => {
    const props = this.props;
    if (type === ACCEPT_TEXT) {
      props.onChange(this.roleListStore.selectedRoles);
      props.onAccept();
    } else if (type === CANCEL_TEXT) {
      props.onReject();
    }
  };

  render() {
    const { localization } = this.props;

    return (
      <Popup visible onClosing={this.onClosing} autoClose={false} type="none">
        <Popup.Header title={localization.getText('ROLE_SETUP_ALL_ROLES')} />
        <Popup.Body>
          <AddRoleGrid {...this.props} store={this.roleListStore} />
        </Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton type="accept">{localization.getText('COMMON_LABEL_OK')}</Popup.ConfirmationButton>
          <Popup.ConfirmationButton type="cancel">
            {localization.getText('COMMON_LABEL_CANCEL')}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/User/Izenda.AddRolePopup.js